import { FC, useEffect } from "react";
import { IconButton } from "@mui/material";
import Close from "@mui/icons-material/Close";
import { format } from "date-fns";
import { useDispatch, useSelector } from "react-redux";
import DarkTooltip from "../../../SolarGikLib/tooltip/DarkTooltip";
import classes from "./SafeOverrideTooltip.module.css";
import { AppDispatch, RootState } from "../../app/Store";
import { fetchSafeOverride } from "../../app/store/SafeOverrideStore";
import { GeneralIcons, IconCategory } from "../../../SolarGikLib/icons/IconsModels";
import getIcons from "../../../SolarGikLib/icons/Icons";

interface ISafeOverrideTooltipProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  siteId: string;
}

const intervalTimeInMilliseconds = 10000;
const SafeOverridePopUpIcon = getIcons(GeneralIcons.SafeOverridePopUp, IconCategory.Status);

const SafeOverrideTooltip: FC<ISafeOverrideTooltipProps> = ({ isOpen, setIsOpen, siteId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { inOverrideSafeMode, expiredAt: remainingTime } = useSelector(
    (state: RootState) => state.safeOverride
  );

  useEffect(() => {
    setIsOpen(inOverrideSafeMode);
  }, [inOverrideSafeMode, setIsOpen]);

  useEffect(() => {
    if (!siteId) {
      return;
    }
    let request: ReturnType<ReturnType<typeof fetchSafeOverride>> | undefined;
    const getOverrideSafeMode = () => {
      if (request) {
        return;
      }
      request = dispatch(fetchSafeOverride(siteId));
      void request.then(() => {
        request = undefined;
      });
    };
    getOverrideSafeMode();
    const intervalId = setInterval(getOverrideSafeMode, intervalTimeInMilliseconds);
    return () => {
      clearInterval(intervalId);
      request?.abort();
    };
  }, [dispatch, siteId]);

  const content = `you are in override mode,
    and not safe in state of high wind or snow
    Ending by \n ${format(remainingTime, "yy-MM-dd HH:mm")}`;

  return (
    <>
      {inOverrideSafeMode && (
        <DarkTooltip
          open={isOpen}
          sx={{
            backgroundColor: "var(--error-color)",
            "& .MuiTooltip-arrow": {
              color: "var(--error-color)",
            },
          }}
          title={
            <>
              <IconButton
                onClick={() => setIsOpen(false)}
                sx={{ position: "absolute", right: "0", top: "0" }}
              >
                <Close sx={{ color: "var(--white)" }} />
              </IconButton>
              {content}
            </>
          }
        >
          <IconButton className={classes["pop-up-container"]} onClick={() => setIsOpen(true)}>
            {<SafeOverridePopUpIcon className={classes["in-override"]} />}
          </IconButton>
        </DarkTooltip>
      )}
    </>
  );
};

export default SafeOverrideTooltip;
